import { useEffect } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { fetchMatrixData, fetchMatrixDataHev } from "api/fetchLtool";
import { authTokenAtom } from "atoms/authAtoms";
import { stageConfigAtom } from "atoms/Ltool/stageConfigAtoms";
import { powertrainAtom } from "atoms/powertrainAtom";
import { gearIdAtom } from "atoms/gearAtom";
import { errorAtom } from "atoms/error";
import { loadingAtom, achievementLevelAtom, } from "atoms/Ltool/matrixAtoms";
import { createEmptyAllMatrixData } from "./initializer";

export function useAllMatrixDataSetter(conditionIds: string[]) {
    const authToken = useAtomValue(authTokenAtom);
    const config = useAtomValue(stageConfigAtom);
    const powertrain = useAtomValue(powertrainAtom);
    const gearId = useAtomValue(gearIdAtom);
    const isHev = powertrain === "hev";
    // gear は HEV の取得にだけ使う。BEV で無関係な gear 変更による再取得を避けるため依存には HEV のときだけ載せる。
    const hevGearId = isHev ? gearId : null;
    const setAllMatrixData = useSetAtom(achievementLevelAtom);
    const setLoading = useSetAtom(loadingAtom);
    const setError = useSetAtom(errorAtom);

    // ローディング状態の初期データをセット
    useEffect(() => {
        const initMap = createEmptyAllMatrixData(conditionIds);
        setAllMatrixData({
            data: initMap,
            status: "notstarted",
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setAllMatrixData]);

    // APIから全条件のデータを取得
    useEffect(() => {
        if (!config.project_id || !authToken) return;
        // HEV は gear 単位 URL で取得する。gear 未選択時は呼ばない（表も出さない）。
        if (isHev && !hevGearId) return;

        // gear 単位の取得では、切替前の gear の遅れた応答が切替後の表示・Excel に混ざる。
        // 取得は全 gear 一括をやめたため、最新の対象以外の応答は破棄する。
        let cancelled = false;

        const fetchAllConditionData = async () => {
            setLoading(true);
            const initMap = createEmptyAllMatrixData(conditionIds);
            setAllMatrixData({
                data: initMap,
                status: "loading",
            });
            try {
                const data = isHev
                    ? await fetchMatrixDataHev(authToken, config, hevGearId!)
                    : await fetchMatrixData(authToken, config);
                if (cancelled) return;
                setAllMatrixData({
                    data,
                    status: "success",
                });
            } catch (unhandled) {
                if (!cancelled) {
                    setError(`Unexpected error in fetching matrix data: ${unhandled}`);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchAllConditionData();

        return () => {
            cancelled = true;
            setLoading(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authToken, config, isHev, hevGearId, setAllMatrixData, setError, setLoading]);
};
